import React, { useEffect, useMemo, useState } from 'react'
import { ChevronDownIcon, ChevronRightIcon } from '@heroicons/react/24/outline'
import type { OtuDetail } from '../../../Generated/OtuDetail.ts'
import type { OtuSourceDetail } from '../../../Generated/OtuSourceDetail.ts'
import type { BlastSource } from '../../../Model/Project/BlastSource.js'
import { fetchOtuSequences } from '../../../Repository/Visualization/OtuCubes.js'
import {
  RANK_LABELS,
  RDP_SUPPORT_THRESHOLD,
  binUrl,
  gbifSpecies,
  ladderCell,
  processIdUrl,
  rdpFails,
  sourceColumns
} from './OtuDetailsDrawer.logic.js'
import type { OtuDetailsState } from './useOtuDetails.js'
import { gbifRedirectPath } from '../Gbif/gbif.js'

/** Members listed at first, and added per "show more". */
export const MEMBER_PAGE = 50

const fixed = (value: number | null | undefined, digits = 2): string => (value == null ? '-' : value.toFixed(digits))

const taxId = (staxids: string | null | undefined): string => {
  const id = staxids?.trim()
  return !id || id === '0' ? '-' : id
}

const external = (href: string, label: React.ReactNode) => (
  <a className="link link-primary" href={href} target="_blank" rel="noopener noreferrer">
    {label}
  </a>
)

type DrawerProps = {
  scopeId: string
  /** The row's OTUs, most reads first. */
  memberIds: readonly string[]
  details: OtuDetailsState
  scopeSources: readonly BlastSource[] | null
}

/** The row's OTUs as stored per reference database: the OTU itself, or a member list for a group of several. */
export function OtuDetailsDrawer({ scopeId, memberIds, details, scopeSources }: DrawerProps) {
  const [shown, setShown] = useState(MEMBER_PAGE)
  const [expanded, setExpanded] = useState<ReadonlySet<string> | null>(null)
  const { cache, failed, request } = details

  const wanted = useMemo(() => memberIds.slice(0, shown), [memberIds, shown])
  useEffect(() => request(wanted), [request, wanted])

  const members = wanted
  const open = expanded ?? new Set(members.slice(0, 1))
  const toggle = (id: string) => {
    const next = new Set(open)
    if (next.has(id)) next.delete(id)
    else next.add(id)
    setExpanded(next)
  }

  if (memberIds.length === 0) return <p className="text-sm text-gray-500">No OTUs to show.</p>

  const status = failed ? (
    <p className="text-sm text-error">
      OTU details could not be loaded.{' '}
      <button className="btn btn-xs btn-outline" onClick={() => request(wanted)}>
        Retry
      </button>
    </p>
  ) : null

  if (memberIds.length === 1) {
    const detail = cache.get(memberIds[0]!)
    return (
      <div data-testid="otu-details-drawer">
        {status}
        {detail === undefined ? (
          <Loading />
        ) : detail === null ? (
          <Gone />
        ) : (
          <OtuDetailPanel scopeId={scopeId} detail={detail} scopeSources={scopeSources} />
        )}
      </div>
    )
  }

  return (
    <div data-testid="otu-details-drawer" className="space-y-1">
      {status}
      <div className="text-xs text-gray-500">
        {memberIds.length.toLocaleString()} OTUs, most reads first
        {memberIds.length > members.length ? `; showing ${members.length.toLocaleString()}` : ''}
      </div>
      <table className="table table-xs">
        <thead>
          <tr>
            <th></th>
            <th>OTU</th>
            <th className="text-right">Cluster size</th>
            <th className="text-right">Total reads</th>
          </tr>
        </thead>
        <tbody>
          {members.map((id) => {
            const detail = cache.get(id)
            const isOpen = open.has(id)
            return (
              <React.Fragment key={id}>
                <tr data-testid="otu-details-member" className="cursor-pointer hover" onClick={() => toggle(id)}>
                  <td className="w-6">{isOpen ? <ChevronDownIcon className="h-4 w-4" /> : <ChevronRightIcon className="h-4 w-4" />}</td>
                  <td className="font-mono">{detail?.otuId ?? (detail === null ? id : '…')}</td>
                  <td className="text-right">{detail ? detail.clusterSize.toLocaleString() : ''}</td>
                  <td className="text-right">{detail ? detail.totalRawReads.toLocaleString() : ''}</td>
                </tr>
                {isOpen && (
                  <tr>
                    <td></td>
                    <td colSpan={3}>
                      {detail === undefined ? (
                        <Loading />
                      ) : detail === null ? (
                        <Gone />
                      ) : (
                        <OtuDetailPanel scopeId={scopeId} detail={detail} scopeSources={scopeSources} />
                      )}
                    </td>
                  </tr>
                )}
              </React.Fragment>
            )
          })}
        </tbody>
      </table>
      {memberIds.length > shown && (
        <button className="btn btn-xs btn-outline" onClick={() => setShown((n) => n + MEMBER_PAGE)}>
          Show {Math.min(MEMBER_PAGE, memberIds.length - shown).toLocaleString()} more
        </button>
      )}
    </div>
  )
}

const Loading = () => <span className="loading loading-spinner loading-sm" />

const Gone = () => <p className="text-sm text-gray-500">This OTU is not in the scope any more.</p>

type PanelProps = { scopeId: string; detail: OtuDetail; scopeSources: readonly BlastSource[] | null }

/** One OTU: its sources side by side with the alternative hit under the top hit, then RDP, BOLD and the sequence. */
export function OtuDetailPanel({ scopeId, detail, scopeSources }: PanelProps) {
  const columns = sourceColumns(detail, scopeSources)
  const gbif = gbifSpecies(columns, detail)
  const stat = (label: string, cell: (s: OtuSourceDetail) => React.ReactNode) => (
    <tr>
      <th scope="row" className="font-normal text-gray-500">
        {label}
      </th>
      {columns.map((s) => (
        <td key={s.source}>{cell(s)}</td>
      ))}
    </tr>
  )

  return (
    <div data-testid="otu-detail-panel" className="space-y-3 py-2">
      <div className="flex flex-wrap items-center gap-4 text-sm">
        <span className="font-mono">{detail.otuId}</span>
        <span>Cluster size {detail.clusterSize.toLocaleString()}</span>
        <span>{detail.totalRawReads.toLocaleString()} reads</span>
        {gbif && external(gbifRedirectPath(gbif), 'GBIF')}
      </div>

      {columns.length === 0 ? (
        <p className="text-sm text-gray-500">No reference database has a hit for this OTU.</p>
      ) : (
        <div className="overflow-x-auto">
          <table data-testid="otu-detail-sources" className="table table-xs table-fixed w-full break-words">
            <thead>
              <tr>
                <th className="w-28"></th>
                {columns.map((s) => (
                  <th key={s.source}>{s.source}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {RANK_LABELS.map((label, rank) => (
                <tr key={label}>
                  <th scope="row" className="font-normal text-gray-500">
                    {label}
                  </th>
                  {columns.map((s) => {
                    const { top, alt } = ladderCell(s, rank)
                    return (
                      <td key={s.source}>
                        <div>{top ?? ''}</div>
                        {alt && (
                          <div data-testid="otu-detail-alt" className="text-gray-400" title="Alternative hit">
                            {alt}
                          </div>
                        )}
                      </td>
                    )
                  })}
                </tr>
              ))}
              {stat('Process ID', (s) => {
                const url = processIdUrl(s)
                return s.processId ? (url ? external(url, s.processId) : s.processId) : '-'
              })}
              {stat('Hit %', (s) => fixed(s.hitPercentage))}
              {stat('Grade %', (s) => fixed(s.gradePercentage))}
              {stat('Query coverage', (s) => (s.queryCoverage == null ? '-' : `${s.queryCoverage} %`))}
              {stat('E-value', (s) => (s.expectationValue == null ? '-' : s.expectationValue.toExponential(2)))}
              {stat('SPARK top / alt', (s) =>
                s.sparkTop == null && s.sparkAlt == null ? '-' : `${fixed(s.sparkTop)} / ${fixed(s.sparkAlt)}`
              )}
              {columns.some((s) => s.description) &&
                stat('Description', (s) =>
                  s.description ? (
                    <div className="truncate" title={s.description}>
                      {s.description}
                    </div>
                  ) : (
                    '-'
                  )
                )}
              {columns.some((s) => taxId(s.staxids) !== '-') && stat('Tax ID', (s) => taxId(s.staxids))}
            </tbody>
          </table>
        </div>
      )}

      <div className="flex flex-wrap gap-6">
        {detail.rdp && (
          <div data-testid="otu-detail-rdp">
            <div className="text-xs font-bold">RDP support</div>
            <table className="table table-xs">
              <tbody>
                {RANK_LABELS.map((label, rank) => {
                  const support = detail.rdp!.support?.[rank]
                  const fails = rdpFails(support)
                  return (
                    <tr key={label}>
                      <th scope="row" className="font-normal text-gray-500">
                        {label}
                      </th>
                      <td
                        data-testid={fails ? 'otu-detail-rdp-fail' : undefined}
                        className={fails ? 'text-error font-bold' : ''}
                        title={fails ? `Below ${RDP_SUPPORT_THRESHOLD}` : undefined}
                      >
                        {fixed(support)}
                      </td>
                    </tr>
                  )
                })}
              </tbody>
            </table>
            {detail.rdp.uniteSpeciesHypothesis && <div className="text-xs">UNITE SH: {detail.rdp.uniteSpeciesHypothesis}</div>}
            {detail.rdp.uniteShUniqueTaxa && <div className="text-xs">SH unique taxa: {detail.rdp.uniteShUniqueTaxa}</div>}
          </div>
        )}
        {detail.bold && <BoldPanel bold={detail.bold} />}
      </div>

      <SequenceView scopeId={scopeId} otuUuid={detail.otuUuid} />
    </div>
  )
}

function BoldPanel({ bold }: { bold: NonNullable<OtuDetail['bold']> }) {
  const url = binUrl(bold)
  const row = (label: string, value: React.ReactNode) => (
    <tr>
      <th scope="row" className="font-normal text-gray-500">
        {label}
      </th>
      <td>{value ?? '-'}</td>
    </tr>
  )
  return (
    <div data-testid="otu-detail-bold">
      <div className="text-xs font-bold">BOLD</div>
      <table className="table table-xs">
        <tbody>
          {row('BIN', url ? external(url, bold.binUri ?? url) : bold.binUri)}
          {row('BIN sharing', bold.binSharing == null ? null : bold.binSharing ? 'Y' : 'N')}
          {row('BIN species', bold.binSpecies?.split('|').join(', '))}
          {row('BIN countries', bold.binCountries)}
          {row('Hit % range', bold.hitPercentageRange)}
          {row('Coordinates', bold.latitude == null || bold.longitude == null ? null : `${bold.latitude}, ${bold.longitude}`)}
          {row('Country', bold.country)}
        </tbody>
      </table>
    </div>
  )
}

function SequenceView({ scopeId, otuUuid }: { scopeId: string; otuUuid: string }) {
  const [sequence, setSequence] = useState<{ status: 'idle' | 'loading' | 'error' } | { status: 'ready'; value: string | null }>({
    status: 'idle'
  })
  const load = () => {
    setSequence({ status: 'loading' })
    fetchOtuSequences(scopeId, [otuUuid])
      .then((found) => setSequence({ status: 'ready', value: found[0]?.fastaSequence ?? null }))
      .catch(() => setSequence({ status: 'error' }))
  }
  if (sequence.status !== 'ready') {
    return (
      <div className="flex items-center gap-2">
        <button
          data-testid="otu-detail-show-sequence"
          className="btn btn-xs btn-outline"
          disabled={sequence.status === 'loading'}
          onClick={load}
        >
          Show sequence
        </button>
        {sequence.status === 'error' && <span className="text-sm text-error">Could not be loaded.</span>}
      </div>
    )
  }
  if (!sequence.value) return <p className="text-sm text-gray-500">No sequence stored.</p>
  return (
    <div className="space-y-1">
      <button className="btn btn-xs btn-outline" onClick={() => void navigator.clipboard?.writeText(sequence.value!)}>
        Copy sequence
      </button>
      <pre
        data-testid="otu-detail-sequence"
        className="font-mono text-xs whitespace-pre-wrap break-all bg-gray-50 rounded p-2 max-h-40 overflow-auto"
      >
        {sequence.value}
      </pre>
    </div>
  )
}
